import React, { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  Timestamp, // ✅ AJOUT IMPORTANT pour corriger les dates
} from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../firebaseConfig";

// 🔥 URL RENDER CORRIGÉE + ROUTE CORRIGÉE
const API_BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://veryfit.onrender.com";

// 🔥 FONCTION POUR PARSER L'ID DE DÉCLARATION
const parseDeclarationId = (declarationId) => {
  if (!declarationId) return { baseProductId: null, unitNumber: null };
  
  if (declarationId.includes('_unit_')) {
    const [baseProductId, unitPart] = declarationId.split('_unit_');
    const unitNumber = parseInt(unitPart) || 1;
    return { baseProductId, unitNumber };
  }
  
  // Si pas d'unité, c'est un produit simple
  return { baseProductId: declarationId, unitNumber: 1 };
};

const DeclarationMontageCarrossierForm = () => {
  // 🔥 CHANGEMENT MAJEUR: declarationId au lieu de productId
  const { orderId, declarationId } = useParams();
  const navigate = useNavigate();
  
  const [form, setForm] = useState({
    nomCarrossier: "",
    dateMontage: "",
    numeroSerie: "",
    observations: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [product, setProduct] = useState(null);
  // 🔥 CORRECTION CLÉE: Utiliser declarationId au lieu de productId
  const [showProductSelection, setShowProductSelection] = useState(!declarationId);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [unitInfo, setUnitInfo] = useState({ baseProductId: null, unitNumber: 1 });
  // 🔥 AJOUT: État pour forcer le re-render quand la signature change
  const [signatureVersion, setSignatureVersion] = useState(0);
  const sigCanvasRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("🔍 Chargement - Dossier:", orderId, "Déclaration:", declarationId);
        
        // 🔥 PARSER L'ID DE DÉCLARATION
        const { baseProductId, unitNumber } = parseDeclarationId(declarationId);
        console.log("🎯 PARSING:", { declarationId, baseProductId, unitNumber });
        setUnitInfo({ baseProductId, unitNumber });
        
        // Réinitialiser le formulaire
        setForm({
          nomCarrossier: "",
          dateMontage: "",
          numeroSerie: "",
          observations: "",
        });
        setSubmitted(false);
        setProduct(null);
        
        // Essayer collection "dossiers"
        let dossierData = null;
        try {
          const dossierRef = doc(db, "dossiers", orderId);
          const dossierSnap = await getDoc(dossierRef);
          if (dossierSnap.exists()) {
            dossierData = dossierSnap.data();
            console.log("📁 Dossier trouvé dans 'dossiers':", dossierData);
          }
        } catch (error) {
          console.log("Collection 'dossiers' non trouvée, essai dans 'orders'");
        }
        
        // Essayer collection "orders"
        if (!dossierData) {
          try {
            const orderRef = doc(db, "orders", orderId);
            const orderSnap = await getDoc(orderRef);
            if (orderSnap.exists()) {
              dossierData = orderSnap.data();
              console.log("📦 Dossier trouvé dans 'orders':", dossierData);
            }
          } catch (error) {
            console.log("Collection 'orders' non trouvée");
          }
        }
        
        if (!dossierData) {
          console.error("❌ Aucun dossier trouvé avec l'ID:", orderId);
          alert(`Dossier ${orderId} non trouvé`);
          return;
        }
        
        setDossier(dossierData);
        
        // 🔥 CRÉER LA LISTE DES PRODUITS AVEC UNITÉS POUR LA SÉLECTION
        const productsWithUnits = [];
        dossierData.produits?.forEach((product) => {
          const quantity = parseInt(product.quantity) || 1;
          const productId = product.productId || product.uuid || product.id;
          
          if (quantity > 1) {
            // Produit avec plusieurs unités
            for (let i = 1; i <= quantity; i++) {
              productsWithUnits.push({
                ...product,
                declarationId: `${productId}_unit_${i}`,
                displayName: `${product.name || product.productName || 'Produit'} - Porte #${i}`,
                unitNumber: i
              });
            }
          } else {
            // Produit unique
            productsWithUnits.push({
              ...product,
              declarationId: productId,
              displayName: product.name || product.productName || 'Produit',
              unitNumber: 1
            });
          }
        });
        
        setAvailableProducts(productsWithUnits);
        console.log("🔍 PRODUITS AVEC UNITÉS:", productsWithUnits);
        
        // Si pas de declarationId, afficher la sélection
        if (!declarationId) {
          setShowProductSelection(true);
          return;
        }
        
        // 🔥 TROUVER LE PRODUIT DE BASE
        let targetProduct = null;
        if (baseProductId && dossierData.produits) {
          targetProduct = dossierData.produits.find(p => 
            p.productId === baseProductId || 
            p.uuid === baseProductId || 
            p.id === baseProductId
          );
          
          if (targetProduct) {
            setProduct(targetProduct);
            setShowProductSelection(false);
            console.log("🎯 Produit trouvé:", targetProduct, "Unité:", unitNumber);
          } else {
            console.error("❌ Produit de base non trouvé:", baseProductId);
            alert(`Produit "${baseProductId}" non trouvé.`);
            return;
          }
        }
        
        // 🔥 VÉRIFIER DÉCLARATION EXISTANTE AVEC LA CLÉ CORRECTE
        const declarationKey = `declarationMontage_${declarationId}`;
        const formDataKey = `${declarationId}_formData`;
        
        if (dossierData[declarationKey] || dossierData[formDataKey]) {
          console.log("✅ Déclaration existante trouvée");
          const existingData = dossierData[declarationKey] || dossierData[formDataKey];
          setForm(existingData);
          setSubmitted(true);
        } else {
          console.log("📝 Nouveau formulaire pour:", declarationId);
          if (targetProduct) {
            setForm(prev => ({
              ...prev,
              numeroSerie: targetProduct.serialNumber || targetProduct.numeroSerie || "",
            }));
          }
        }
        
      } catch (error) {
        console.error("❌ Erreur lors du chargement:", error);
        alert("Erreur lors du chargement: " + error.message);
      }
    };
    
    if (orderId) {
      fetchData();
    }
  }, [orderId, declarationId]);

  const handleProductSelect = (selectedDeclarationId) => {
    console.log("🎯 Sélection de la déclaration:", selectedDeclarationId);
    navigate(`/carrossier/orders/${orderId}/declaration-montage/${selectedDeclarationId}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`🔄 Changement ${name}:`, value);
    setForm((prev) => ({ ...prev, [name]: value }));
    
    if (name === 'dateMontage') {
      console.log("📅 Date sélectionnée:", value);
    }
  };

  // 🔥 NOUVELLE FONCTION: Gestion des changements de signature
  const handleSignatureChange = () => {
    console.log("✍️ Signature modifiée");
    setSignatureVersion(prev => prev + 1); // Force le re-render
  };

  const handleClear = () => {
    sigCanvasRef.current.clear();
    handleSignatureChange(); // 🔥 Force le re-render après effacement
  };

  // 🔥 VALIDATION AMÉLIORÉE avec dependency sur signatureVersion
  const isFormValid = () => {
    console.log("🔍 Validation avec signature version:", signatureVersion);
    
    const hasCarrossier = Boolean(form.nomCarrossier?.trim());
    const hasDate = Boolean(form.dateMontage?.trim()) && 
                    form.dateMontage !== "" && 
                    /^\d{4}-\d{2}-\d{2}$/.test(form.dateMontage);
    const hasSerial = Boolean(form.numeroSerie?.trim());
    const hasSignature = sigCanvasRef.current && !sigCanvasRef.current.isEmpty();
    
    console.log("📋 Validations détaillées:", {
      hasCarrossier,
      hasDate,
      hasSerial,
      hasSignature,
      signatureVersion,
      dateMontage: form.dateMontage
    });
    
    return hasCarrossier && hasDate && hasSerial && hasSignature;
  };

  // 🔥 CORRECTION MAJEURE: Utilisation de Timestamp.now() au lieu de new Date()
  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("⚠️ Veuillez remplir tous les champs obligatoires et signer le document");
      return;
    }

    if (!declarationId) {
      alert("❌ ID de déclaration manquant. Impossible de sauvegarder.");
      return;
    }

    setLoading(true);
    try {
      console.log("💾 Sauvegarde - Dossier:", orderId, "Déclaration:", declarationId);
      
      const signatureData = sigCanvasRef.current
        ? sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png")
        : null;

      const fullForm = {
        ...form,
        signature: signatureData,
        submittedAt: Timestamp.now(), // ✅ CORRECTION: Timestamp.now() au lieu de new Date()
        dossierId: orderId,
        declarationId: declarationId,
        unitNumber: unitInfo.unitNumber,
        baseProductId: unitInfo.baseProductId,
      };

      let updateSuccess = false;
      
      // 🔥 CORRECTION: Génération des clés UNIQUES pour chaque déclaration
      const declarationKey = `declarationMontage_${declarationId}`;
      const formDataKey = `${declarationId}_formData`;
      const statusKey = `${declarationId}_status`;
      const filledKey = `${declarationId}_filled`;
      const montageCompleteKey = `${declarationId}_montage_complete`;

      // 🔥 DEBUG: Vérifier les clés générées
      console.log("🔑 CLÉS GÉNÉRÉES:", {
        declarationId,
        declarationKey,
        formDataKey,
        statusKey,
        filledKey,
        montageCompleteKey,
        unitNumber: unitInfo.unitNumber,
        baseProductId: unitInfo.baseProductId
      });

      // 🔥 IMPORTANTE: Vérifier que declarationId contient bien l'unité
      if (!declarationId.includes('_unit_') && unitInfo.unitNumber > 1) {
        console.error("❌ ERREUR: L'ID de déclaration ne contient pas l'unité pour une quantité multiple!");
        console.error("declarationId:", declarationId, "unitNumber:", unitInfo.unitNumber);
        alert(`Erreur: ID de déclaration incorrect pour la porte #${unitInfo.unitNumber}. ID reçu: ${declarationId}`);
        setLoading(false);
        return;
      }

      // 1. Mise à jour collection "dossiers"
      try {
        const dossierRef = doc(db, "dossiers", orderId);
        const dossierSnap = await getDoc(dossierRef);
        if (dossierSnap.exists()) {
          // 🔥 CORRECTION: Mise à jour ATOMIQUE avec toutes les clés spécifiques
          const updateData = {
            [declarationKey]: fullForm,
            [formDataKey]: fullForm,
            [statusKey]: "declaration_montage_complete",
            [filledKey]: true,
            [montageCompleteKey]: true,
            lastUpdated: Timestamp.now(), // ✅ CORRECTION: Timestamp.now()
          };

          console.log("📝 MISE À JOUR DOSSIER avec les données:", updateData);
          
          await updateDoc(dossierRef, updateData);
          
          console.log("✅ Mis à jour dans 'dossiers' pour déclaration:", declarationId);
          console.log("🔥 CLÉ MONTAGE_COMPLETE MISE À TRUE:", montageCompleteKey);
          updateSuccess = true;
        }
      } catch (error) {
        console.log("Collection 'dossiers' non accessible:", error.message);
      }

      // 2. Mise à jour collection "orders"
      try {
        const orderRef = doc(db, "orders", orderId);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const updateData = {
            [declarationKey]: fullForm,
            [formDataKey]: fullForm,
            [statusKey]: "declaration_montage_complete",
            [filledKey]: true,
            [montageCompleteKey]: true,
            lastUpdated: Timestamp.now(), // ✅ CORRECTION: Timestamp.now()
          };

          console.log("📝 MISE À JOUR ORDER avec les données:", updateData);
          
          await updateDoc(orderRef, updateData);
          
          console.log("✅ Mis à jour dans 'orders' pour déclaration:", declarationId);
          console.log("🔥 CLÉ MONTAGE_COMPLETE MISE À TRUE:", montageCompleteKey);
          updateSuccess = true;
        }
      } catch (error) {
        console.log("Collection 'orders' non accessible:", error.message);
      }

      if (!updateSuccess) {
        throw new Error(`Impossible de mettre à jour le dossier ${orderId} pour la déclaration ${declarationId}`);
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      // 3. 🔥 GÉNÉRATION PDF AVEC ROUTE CORRIGÉE POUR UNITÉS
      try {
        const pdfUrl = `${API_BASE_URL}/api/generate/declaration-montage-carrossier/${orderId}/${declarationId}`;
        console.log("📄 Génération PDF:", pdfUrl);
        
        const response = await fetch(pdfUrl, { method: "GET" });

        if (!response.ok) {
          console.warn("PDF non généré mais déclaration sauvegardée - Status:", response.status);
        } else {
          console.log("✅ PDF généré avec succès");
        }
      } catch (pdfError) {
        console.warn("Erreur PDF (non bloquante):", pdfError);
      }

      // 4. Notification
      await addDoc(collection(getFirestore(), "notifications"), {
        type: "declarationMontage_carrossier",
        dossierId: orderId,
        declarationId: declarationId,
        productId: unitInfo.baseProductId,
        productName: product?.name || product?.productName || "Produit",
        unitNumber: unitInfo.unitNumber,
        message: `🧾 Déclaration de montage pour ${product?.name || 'le produit'} - Porte #${unitInfo.unitNumber} (${declarationId}) du dossier ${orderId}`,
        carrossier: form.nomCarrossier,
        dateMontage: form.dateMontage,
        numeroSerie: form.numeroSerie,
        read: false,
        createdAt: Timestamp.now(), // ✅ CORRECTION: Timestamp.now()
      });

      console.log("🎉 Déclaration sauvegardée avec succès pour la déclaration:", declarationId);
      setSubmitted(true);
      
    } catch (error) {
      console.error("❌ Erreur enregistrement :", error);
      alert("❌ Une erreur est survenue : " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 ÉCRAN DE SÉLECTION DU PRODUIT AVEC UNITÉS (seulement si pas de declarationId)
  if (showProductSelection && !declarationId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 via-red-50 to-blue-50 p-8 border-b border-gray-100">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  🔧 Sélectionner le produit
                </h2>
                <p className="text-gray-600">
                  Choisissez le produit/unité pour lequel vous souhaitez remplir la déclaration de montage.
                </p>
              </div>
            </div>
            
            <div className="p-8">
              <div className="grid gap-4">
                {availableProducts.map((productUnit, index) => (
                  <button
                    key={productUnit.declarationId || index}
                    onClick={() => handleProductSelect(productUnit.declarationId)}
                    className="flex items-center justify-between p-6 border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900 text-lg">
                        🚪 {productUnit.displayName}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        ID: {productUnit.declarationId}
                      </p>
                    </div>
                    <div className="text-blue-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
              
              {availableProducts.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Aucun produit trouvé dans ce dossier.</p>
                </div>
              )}
              
              <div className="mt-8 text-center">
                <button
                  onClick={() => navigate(-1)}
                  className="bg-gray-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                >
                  ← Retour
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🔥 ÉCRAN DE SUCCÈS
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            
            <div className="bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 p-8 text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                ✅ Déclaration enregistrée avec succès
              </h1>
              <p className="text-gray-600 text-lg mb-2">
                Votre déclaration de montage pour la <strong>Porte #{unitInfo.unitNumber}</strong> a été transmise à FIT.
              </p>
              <p className="text-gray-600 text-lg mb-8">
                Elle sera traitée dans les plus brefs délais.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <span>←</span>
                  Retour au dossier
                </button>
                <button
                  onClick={() => navigate('/carrossier')}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-lg"
                >
                  <span>🏠</span>
                  Tableau de bord
                </button>
              </div>
            </div>

            <div className="p-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">📋 Récapitulatif de votre déclaration</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Produit</label>
                    <p className="text-gray-900 font-semibold">
                      🚪 {product?.name || product?.productName || unitInfo.baseProductId} - Porte #{unitInfo.unitNumber}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Carrossier</label>
                    <p className="text-gray-900 font-semibold">{form.nomCarrossier}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Date de montage</label>
                    <p className="text-gray-900 font-semibold">
                      {new Date(form.dateMontage).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Numéro de série de la porte #{unitInfo.unitNumber}</label>
                    <p className="text-gray-900 font-semibold font-mono">{form.numeroSerie}</p>
                  </div>
                  {form.observations && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Observations</label>
                      <p className="text-gray-900">{form.observations}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🔥 FORMULAIRE PRINCIPAL AVEC TITRE DYNAMIQUE
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100">
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              >
                <span className="text-lg">←</span>
                <span className="font-medium">Retour</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">🧾</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    Déclaration de montage - Porte #{unitInfo.unitNumber}
                  </h1>
                  <p className="text-sm text-gray-600">
                    Dossier: {dossier?.orderName || orderId}
                    {product && (
                      <span className="block text-blue-600 font-medium">
                        🚪 {product.name || product.productName || unitInfo.baseProductId} - Porte #{unitInfo.unitNumber}
                      </span>
                    )}
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded mt-1 inline-block">
                      {orderId} / {declarationId}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 via-red-50 to-blue-50 p-8 border-b border-gray-100">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                🔧 Déclaration de montage carrossier - Porte #{unitInfo.unitNumber}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Veuillez compléter cette déclaration pour attester du bon montage de la <strong>Porte #{unitInfo.unitNumber}</strong>. 
                Tous les champs marqués d'un astérisque (*) sont obligatoires.
              </p>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-8">
              
              {/* Informations générales */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <span className="text-blue-600">📝</span>
                  Informations générales
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du carrossier *
                    </label>
                    <input
                      name="nomCarrossier"
                      type="text"
                      placeholder="Nom complet ou raison sociale"
                      value={form.nomCarrossier}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500 transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de montage *
                    </label>
                    <input
                      name="dateMontage"
                      type="date"
                      value={form.dateMontage}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                  </div>
                </div>
                </div>

              {/* Informations produit */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <span className="text-red-600">🚪</span>
                  Informations produit - Porte #{unitInfo.unitNumber}
                </h3>
                
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numéro de série de la porte #{unitInfo.unitNumber} *
                    </label>
                    <input
                      name="numeroSerie"
                      type="text"
                      placeholder={`Numéro de série de la porte #${unitInfo.unitNumber}`}
                      value={form.numeroSerie}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500 font-mono transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observations (facultatif)
                    </label>
                    <textarea
                      name="observations"
                      placeholder={`Commentaires, remarques particulières sur le montage de la porte #${unitInfo.unitNumber}...`}
                      value={form.observations}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500 resize-none transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Signature */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <span className="text-blue-600">✍️</span>
                  Signature du carrossier *
                </h3>
                
                <div className="bg-blue-50 rounded-xl p-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Veuillez signer dans l'espace ci-dessous pour certifier les informations déclarées pour la Porte #{unitInfo.unitNumber}.
                  </p>
                  
                  <div className="border-2 border-dashed border-blue-300 rounded-xl p-4 bg-white">
                    <SignatureCanvas
                      ref={sigCanvasRef}
                      penColor="black"
                      onEnd={handleSignatureChange} // 🔥 AJOUT: Déclenche re-render à chaque trait
                      canvasProps={{
                        width: 600,
                        height: 200,
                        className: "sigCanvas w-full h-48 cursor-crosshair",
                      }}
                    />
                  </div>
                  
                  <div className="flex justify-between items-center mt-4">
                    <p className="text-sm text-gray-500">
                      Signez avec votre souris, trackpad ou écran tactile
                    </p>
                    <button
                      onClick={handleClear}
                      className="text-sm text-red-600 hover:text-red-700 font-medium hover:bg-red-50 px-3 py-1 rounded-lg transition-colors"
                    >
                      🗑️ Effacer
                    </button>
                  </div>
                </div>
              </div>

              {/* Déclaration sur l'honneur */}
              <div className="bg-gradient-to-r from-blue-50 to-red-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="text-blue-600 text-xl mt-1">⚠️</div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Déclaration sur l'honneur</h4>
                    <p className="text-blue-700 text-sm">
                      En soumettant cette déclaration, je certifie sur l'honneur que le montage de la <strong>Porte #{unitInfo.unitNumber}</strong> a été effectué 
                      conformément aux instructions du fabricant et aux normes en vigueur. Cette déclaration 
                      engage ma responsabilité professionnelle.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bouton de soumission */}
              <div className="flex justify-center pt-6">
                <button
                  onClick={handleSubmit}
                  disabled={loading || !isFormValid()}
                  className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform ${
                    loading || !isFormValid()
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-red-600 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] hover:from-blue-700 hover:to-red-700"
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Enregistrement en cours...
                    </>
                  ) : (
                    <>
                      <span className="text-xl">✅</span>
                      Enregistrer Porte #{unitInfo.unitNumber}
                    </>
                  )}
                </button>
              </div>

              {!isFormValid() && (
                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    Veuillez remplir tous les champs obligatoires (*) et signer le document
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeclarationMontageCarrossierForm;